/**
 * Preview generation. Given a decoded image (as an ImageBitmap), produce a
 * downscaled JPEG (max edge 2048, quality 0.82) for upload + on-screen preview.
 * All inputs are already EXIF-orientation-corrected by the decoders.
 */

export const PREVIEW_MAX_EDGE = 2048;
export const PREVIEW_QUALITY = 0.82;

interface Canvas2D {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;
}

function makeCanvas(width: number, height: number): Canvas2D {
  if (typeof OffscreenCanvas !== 'undefined') {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable (OffscreenCanvas)');
    return { canvas, ctx };
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  return { canvas, ctx };
}

async function canvasToBlob(
  canvas: OffscreenCanvas | HTMLCanvasElement,
  quality: number,
): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}

/** Turn raw RGBA pixels (from TIFF/HEIC decoders) into an ImageBitmap. */
export async function bitmapFromImageData(data: ImageData): Promise<ImageBitmap> {
  return createImageBitmap(data);
}

/** Downscale an ImageBitmap to a preview JPEG Blob. */
export async function makePreviewBlob(
  source: ImageBitmap,
  maxEdge = PREVIEW_MAX_EDGE,
  quality = PREVIEW_QUALITY,
): Promise<Blob> {
  const scale = Math.min(1, maxEdge / Math.max(source.width, source.height));
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const { canvas, ctx } = makeCanvas(w, h);
  ctx.drawImage(source, 0, 0, w, h);
  return canvasToBlob(canvas, quality);
}
